import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {Gateway} from '@models/adminstration/gateway/gateway';
import {GATEWAY_TYPE} from '@constants/gateway/gateway-type';
import {GatewayIot} from '@models/adminstration/gateway/gateway-configuration/gateway-iot';
import {UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {REGIONS} from '@constants/administration/gateway/iot-core-regions';
import {GatewayService} from '@services/administration/gateway/gateway.service';
import {NotificationService} from '@services/notification/notification.service';
import {debounceTime, takeUntil} from 'rxjs/operators';
import {Subject} from 'rxjs';
import {ElasticSearchService} from '@services/elastic-search/elastic-search.service';
import {BIGQUERY_VALID_NAME_REGEX} from '@constants/reg-exp';


@Component({
    selector: 'scitis-machine-default-gateway',
    templateUrl: './machine-default-gateway.component.html',
    styleUrls: ['./machine-default-gateway.component.scss'],
    standalone: false
})
export class MachineDefaultGatewayComponent implements OnInit, OnDestroy {

  @Input()
  isAddOperation: boolean = false;

  @Input()
  organizationId: number = -1;

  @Input()
  machineTemplate: boolean = false;

  @Input()
  isAdminOrHigher: boolean = false;

  @Input()
  machineTag: string | undefined;

  @Output()
  defaultGatewayDetailsChange: EventEmitter<Gateway> = new EventEmitter<Gateway>();

  @Output()
  gatewayConfigurationIssues: EventEmitter<boolean> = new EventEmitter<boolean>();

  possibleGatewayTypes: string[] = Object.values(GATEWAY_TYPE);
  defaultRegion = REGIONS.EUROPE;

  gateway: Gateway;
  gatewayForm: UntypedFormGroup = new UntypedFormGroup({});
  loadRegistryIds: boolean = false;
  iotCoreRegistryIds: string[] = new Array<string>();
  // convenience getter for easy access to form fields
  public isVerifyUsage: boolean = false;

  private destroy: Subject<boolean> = new Subject<boolean>();

  constructor(private gatewayService: GatewayService,
              private elasticSearchService: ElasticSearchService,
              private notification: NotificationService) {
    this.gateway = new Gateway();

  }

  get f() {
    return this.gatewayForm.controls;
  }

  public get shouldBeVisible(): boolean {
    return this.isAddOperation && !this.machineTemplate && !!this.machineTag && this.machineTag.length > 0;
  }

  ngOnInit(): void {
    this.generateGatewayForm(this.gateway);
    this.onGatewayDetailsUpdated();
  }

  ngOnDestroy(): void {
    this.destroy.next(true);
    this.destroy.complete();
  }

  public onGatewayTypeChange(type: GATEWAY_TYPE) {
    if (type === GATEWAY_TYPE.CPLIGHT || type === GATEWAY_TYPE.GENERIC) {
      this.gateway.iotCore = new GatewayIot();
      this.listRegistryIds();
    }
    this.gateway.cloudplugType = type;
    this.updateFormValidationByGatewayType(type);
  }


  public onSerialNumberChange() {
    const serial: string = this.f.serial.value;
    if (!serial || serial.length === 0 || serial.trim().length === 0 || this.f.serial.errors) {
      return;
    }
    this.gatewayAlreadyExists(serial);
  }

  public isGenericGatewayOrCloudplugLight(): boolean {
    return this.gateway.cloudplugType === GATEWAY_TYPE.CPLIGHT || this.gateway.cloudplugType === GATEWAY_TYPE.GENERIC;
  }

  private generateGatewayForm(gateway: Gateway) {
    this.gatewayForm = new UntypedFormGroup({
      gatewayType: new UntypedFormControl(gateway.cloudplugType, [Validators.required]),
      name: new UntypedFormControl(gateway.name, [Validators.required, Validators.pattern(BIGQUERY_VALID_NAME_REGEX)]),
      serial: new UntypedFormControl(gateway.id, [Validators.required, Validators.min(5)]),
      region: new UntypedFormControl(this.defaultRegion),
      registry: new UntypedFormControl(),
    });

    this.gatewayForm.valueChanges
    .pipe(takeUntil(this.destroy), debounceTime(500))
    .subscribe(() => this.onGatewayDetailsUpdated());

    if (!this.isAdminOrHigher) {
      this.gatewayForm.disable();
    }
  }

  private updateFormValidationByGatewayType(type: GATEWAY_TYPE) {
    if (type === GATEWAY_TYPE.CPLIGHT || type === GATEWAY_TYPE.GENERIC) {
      this.f.region.setValidators(Validators.required);
      this.f.registry.setValidators(Validators.required);
    } else {
      this.f.region.clearValidators();
      this.f.registry.clearValidators();
    }
  }

  private gatewayAlreadyExists(serial: string) {
    this.isVerifyUsage = true;
    this.elasticSearchService.isGatewayPropertyAlreadyUsed(serial, true).subscribe({
      next: (response) => {
        if (response?.result?.totalHits > 0) {

          this.f.serial.setErrors({used: true});
        } else {
          this.f.serial.setErrors(null);
        }
        this.isVerifyUsage = false;
      }, error: (error) => {
        this.notification.error('Cannot check for gateway ' + serial + ' existence', error, 'Machine default gateway', true);
        this.f.serial.setErrors({used: false});
        this.isVerifyUsage = false;
      }
    });
  }

  private listRegistryIds() {
    this.loadRegistryIds = true;
    this.gatewayService.getIotRegistryIds().subscribe({
      next: registryIds => {
        this.iotCoreRegistryIds = registryIds;
        this.loadRegistryIds = false;
      }, error: error => {
        console.log(error);
        this.loadRegistryIds = false;
      }
    });
  }

  private onGatewayDetailsUpdated() {
    if (!this.shouldBeVisible) {
      this.gatewayConfigurationIssues.emit(false);
      return;
    }
    const gateway = new Gateway();
    gateway.cloudplugType = this.f.gatewayType.value;
    gateway.id = this.f.serial.value;
    gateway.name = this.f.name.value;
    gateway.iotCore = new GatewayIot();
    gateway.iotCore.cloudRegion = this.f.region.value;
    gateway.iotCore.registryId = this.f.registry.value;

    this.defaultGatewayDetailsChange.emit(gateway);
    this.gatewayConfigurationIssues.emit(this.gatewayForm.invalid);
  }
}
